import axios from 'axios';
import { API_BASE_URL } from './config';

// Interfaces
export interface Issue {
  _id: string;
  title: string;
  description: string;
  category: 'safety' | 'quality' | 'schedule' | 'budget' | 'technical' | 'other';
  priority: 'low' | 'medium' | 'high' | 'critical';
  status: 'open' | 'in-progress' | 'resolved' | 'closed' | 'on-hold';
  project: string;
  location: string;
  reportedBy: {
    _id: string;
    name: string;
    email: string;
    role: string;
  };
  assignedTo?: {
    _id: string;
    name: string;
    email: string;
    role: string;
  };
  dueDate?: string;
  resolvedDate?: string;
  resolution?: string;
  attachments: {
    _id: string;
    filename: string;
    originalName: string;
    url: string;
    type: string;
    size: number;
  }[];
  comments: {
    _id: string;
    author: {
      _id: string;
      name: string;
      email: string;
    };
    content: string;
    createdAt: string;
  }[];
  tags: string[];
  estimatedHours?: number;
  actualHours?: number;
  cost?: number;
  impact: 'low' | 'medium' | 'high' | 'critical';
  createdAt: string;
  updatedAt: string;
}

export interface IssueComment {
  _id: string;
  issueId: string;
  author: {
    _id: string;
    name: string;
    email: string;
  };
  content: string;
  attachments?: {
    _id: string;
    filename: string;
    url: string;
    type: string;
  }[];
  createdAt: string;
  updatedAt: string;
}

export interface IssueFilters {
  status?: string;
  priority?: string;
  category?: string;
  project?: string;
  assignedTo?: string;
  reportedBy?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface IssueStats {
  totalIssues: number;
  openIssues: number;
  inProgressIssues: number;
  resolvedIssues: number;
  closedIssues: number;
  criticalIssues: number;
  highPriorityIssues: number;
  averageResolutionTime: number;
  issuesByCategory: {
    category: string;
    count: number;
  }[];
  issuesByProject: {
    project: string;
    count: number;
  }[];
}

// Axios instance with auth token
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Issue Tracking Service
class IssueTrackingService {
  // Issue Management
  async getIssues(filters?: IssueFilters): Promise<Issue[]> {
    // Backend mounts issue tracking routes at /api/issue-tracking
    const response = await api.get('/issue-tracking', { params: filters });
    const data = response?.data;
    const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
    return list;
  }

  async getIssue(id: string): Promise<Issue> {
    const response = await api.get(`/issues/${id}`);
    return response.data;
  }

  async createIssue(issue: Omit<Issue, '_id' | 'createdAt' | 'updatedAt' | 'comments'>): Promise<Issue> {
    // Backend mounts issue tracking routes at /api/issue-tracking
    const response = await api.post('/issue-tracking', issue);
    return response.data;
  }

  async updateIssue(id: string, issue: Partial<Issue>): Promise<Issue> {
    const response = await api.put(`/issues/${id}`, issue);
    return response.data;
  }

  async deleteIssue(id: string): Promise<void> {
    await api.delete(`/issues/${id}`);
  }

  // Issue Status Management
  async updateIssueStatus(id: string, status: Issue['status'], resolution?: string): Promise<Issue> {
    const response = await api.patch(`/issues/${id}/status`, { status, resolution });
    return response.data;
  }

  async assignIssue(id: string, assignedTo: string): Promise<Issue> {
    const response = await api.patch(`/issues/${id}/assign`, { assignedTo });
    return response.data;
  }

  async updatePriority(id: string, priority: Issue['priority']): Promise<Issue> {
    const response = await api.patch(`/issues/${id}/priority`, { priority });
    return response.data;
  }

  // Comments Management
  async getIssueComments(issueId: string): Promise<IssueComment[]> {
    const response = await api.get(`/issues/${issueId}/comments`);
    return response.data;
  }

  async addComment(issueId: string, content: string, attachments?: File[]): Promise<IssueComment> {
    const formData = new FormData();
    formData.append('content', content);
    
    if (attachments) {
      attachments.forEach((file, index) => {
        formData.append(`attachments`, file);
      });
    }

    const response = await api.post(`/issues/${issueId}/comments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async updateComment(issueId: string, commentId: string, content: string): Promise<IssueComment> {
    const response = await api.put(`/issues/${issueId}/comments/${commentId}`, { content });
    return response.data;
  }

  async deleteComment(issueId: string, commentId: string): Promise<void> {
    await api.delete(`/issues/${issueId}/comments/${commentId}`);
  }

  // Attachments Management
  async uploadAttachment(issueId: string, file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(`/issues/${issueId}/attachments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async deleteAttachment(issueId: string, attachmentId: string): Promise<void> {
    await api.delete(`/issues/${issueId}/attachments/${attachmentId}`);
  }

  async downloadAttachment(issueId: string, attachmentId: string): Promise<Blob> {
    const response = await api.get(`/issues/${issueId}/attachments/${attachmentId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Statistics and Reports
  async getStats(filters?: IssueFilters): Promise<IssueStats> {
    const response = await api.get('/issues/stats', { params: filters });
    return response.data;
  }

  async getIssuesByProject(projectId: string): Promise<Issue[]> {
    const response = await api.get(`/issues/project/${projectId}`);
    return response.data;
  }

  async getIssuesByUser(userId: string, type: 'assigned' | 'reported' = 'assigned'): Promise<Issue[]> {
    const response = await api.get(`/issues/user/${userId}`, { params: { type } });
    return response.data;
  }

  // Bulk Operations
  async bulkUpdateStatus(issueIds: string[], status: Issue['status']): Promise<void> {
    await api.patch('/issues/bulk-status', { issueIds, status });
  }

  async bulkAssign(issueIds: string[], assignedTo: string): Promise<void> {
    await api.patch('/issues/bulk-assign', { issueIds, assignedTo });
  }

  async bulkDelete(issueIds: string[]): Promise<void> {
    await api.delete('/issues/bulk-delete', { data: { issueIds } });
  }

  // Export
  async exportIssues(filters?: IssueFilters, format: 'csv' | 'excel' | 'pdf' = 'csv'): Promise<Blob> {
    const response = await api.get('/issues/export', {
      params: { ...filters, format },
      responseType: 'blob',
    });
    return response.data;
  }

  // Search
  async searchIssues(query: string, filters?: IssueFilters): Promise<Issue[]> {
    const response = await api.get('/issues/search', {
      params: { q: query, ...filters },
    });
    return response.data;
  }
}

export const issueTrackingService = new IssueTrackingService();
export default issueTrackingService;